import { useMemo } from "react";
import { Spin } from "antd";
import type { MenuProps } from "antd";
import classNames from "classnames";
import {
  UserOutlined,
  CheckOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import { useAppSelector } from "@stores/redux/store";
import type { Agent } from "@interfaces/api.interface";
import styles from "../../styles.module.less";

interface UseAgentSubmenuParams {
  agents: Agent[];
  loadingAgents: boolean;
  selectedAgent?: Agent;
  conversationId?: string;
  onAgentSelect: (agentId: number) => void;
  onAgentRemove?: () => void;
}

export const useAgentSubmenu = ({
  agents,
  loadingAgents,
  selectedAgent,
  conversationId,
  onAgentSelect,
  onAgentRemove,
}: UseAgentSubmenuParams): MenuProps["items"] => {
  const agentsByConversation = useAppSelector(
    (state) => state.agents.agentsByConversation
  );

  const currentAgent = useMemo(() => {
    if (conversationId && agentsByConversation[conversationId]) {
      return agentsByConversation[conversationId];
    }
    return selectedAgent;
  }, [conversationId, agentsByConversation, selectedAgent]);

  return useMemo(() => {
    if (loadingAgents) {
      return [
        {
          key: "loading",
          label: (
            <div className={styles.menuLoading}>
              <Spin size="small" />
            </div>
          ),
          disabled: true,
        },
      ];
    }

    const items: MenuProps["items"] = [];

    if (agents.length === 0) {
      if (items.length === 0) {
        items.push({
          key: "empty",
          label: <div className={styles.menuEmpty}>Không có agent nào</div>,
          disabled: true,
        });
      }
      return items;
    }

    const agentItems = agents.map((agentItem) => {
      const isSelected = currentAgent?.id === agentItem.id;

      return {
        key: `agent-${agentItem.id}`,
        label: (
          <div
            className={classNames(styles.tagSubmenuItem, {
              [styles.tagSubmenuItemSelected]: isSelected,
            })}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              onAgentSelect(agentItem.id);
            }}
          >
            <UserOutlined className={styles.tagSubmenuIcon} />
            <span className={styles.tagSubmenuLabel}>{agentItem.name}</span>
            {isSelected && <CheckOutlined className={styles.tagSubmenuCheck} />}
          </div>
        ),
        className: isSelected ? styles.tagMenuItemSelected : undefined,
        onClick: ({
          domEvent,
        }: {
          domEvent?: React.MouseEvent | React.KeyboardEvent;
        }) => {
          domEvent?.stopPropagation();
          if (domEvent && "preventDefault" in domEvent) {
            domEvent.preventDefault();
          }
          // Return false để ngăn menu đóng
          return false;
        },
      };
    });

    const result: MenuProps["items"] = [...agentItems];
    if (currentAgent && onAgentRemove) {
      result.push({
        type: "divider",
      });
      result.push({
        key: "remove-agent-footer",
        label: (
          <div className={styles.agentSubmenuFooter}>
            <button
              className={styles.agentRemoveButton}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAgentRemove();
              }}
            >
              <UserDeleteOutlined />
              <span>Gỡ agent</span>
            </button>
          </div>
        ),
        disabled: true,
        style: { padding: 0, cursor: 'default' },
      });
    }

    return result;
  }, [agents, loadingAgents, currentAgent, onAgentSelect, onAgentRemove]);
};
